import { LookbookSettingsLayout, LookbookSettingsNavigationMenu } from '@app/components/lookbook/LookbookSettingsPage/components';
import { LookbookNavigationSettingsContextProvider } from '@app/components/lookbook/LookbookSettingsPage/contexts';
import { addToast } from '@app/components/various/Toasts';
import { compiledPaths } from '@app/paths';
import { LOOKBOOK_BY_ID_QUERY_KEY, useLookbookDetails } from '@app/services/hooks/lookbook/useLookbookDetails';
import { useUpdateLookbookPages } from '@app/services/hooks/lookbook/useUpdateLookbookPages';
import { queryClient } from '@app/services/queryClient';
import { isDefined } from '@app/utils/is';
import { Lookbook } from '@typings/@centra';
import { equals } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export const LookbookSettingsNavigationPage = () => {
  const { lookbookId } = useParams<{ lookbookId: string }>();
  const { t } = useTranslation(['lookbook', 'settings']);
  const { mutate, isPending } = useUpdateLookbookPages();
  const [navigationItems, setNavigationItems] = React.useState<Lookbook.NavigationItem[]>([]);
  const { data, isLoading } = useLookbookDetails({ lookbookId });

  const initialNavigationItems = React.useMemo(() => {
    return data?.navigationItems ?? [];
  }, [data?.navigationItems]);

  const isDirty = React.useMemo(() => !equals(initialNavigationItems, navigationItems), [initialNavigationItems, navigationItems]);

  const handleSubmit = () => {
    if (!isDefined(lookbookId)) {
      return;
    }

    mutate(
      {
        data: {
          items: navigationItems,
        },
        lookbookId,
      },
      {
        onSuccess: () => {
          addToast(t('settings:navigation_updated'), { type: 'success' });
          queryClient.invalidateQueries({ queryKey: [LOOKBOOK_BY_ID_QUERY_KEY, { lookbookId }] });
        },
      },
    );
  };

  return (
    <LookbookSettingsLayout
      lookbookId={lookbookId!}
      activePath={compiledPaths.LOOKBOOK_SINGLE_SETTINGS_NAVIGATION({ lookbookId: lookbookId! })}
      isDisabled={isPending || isLoading}
      isSubmitting={isPending || isLoading}
      onSubmit={handleSubmit}
      hasChanges={isDirty}
      onReset={() => setNavigationItems(initialNavigationItems)}
    >
      <LookbookNavigationSettingsContextProvider
        lookbookId={lookbookId}
        setNavigationItems={setNavigationItems}
        addedMenuItems={navigationItems}
      >
        <section className="box space-y-5">
          <h3 className="heading-3">{t('lookbook:navigation_menu')}</h3>
          <p className="text-neutral-40 body-2">{t('lookbook:navigation_menu_description')}</p>
          <LookbookSettingsNavigationMenu lookbookId={lookbookId} />
        </section>
      </LookbookNavigationSettingsContextProvider>
    </LookbookSettingsLayout>
  );
};
